import { useCallback, useEffect, useRef } from "react";

import type { CurriculumAudioPlayer } from "./useCurriculumAudioPlayer";

export interface CurriculumFeedbackPlayback {
  readonly audioPath: string;
  readonly playbackRequest: number | string;
}

export interface CurriculumFeedbackAudioState {
  readonly isPlaying: boolean;
  readonly replay: () => void;
}

export function useCurriculumFeedbackAudio(
  player: CurriculumAudioPlayer,
  feedback: CurriculumFeedbackPlayback | null,
  enabled = true
): CurriculumFeedbackAudioState {
  const playedRequestRef = useRef<string | null>(null);
  const audioPath = feedback?.audioPath ?? null;
  const playbackRequest = feedback?.playbackRequest ?? null;
  const { isPlayingPath, playPath } = player;

  useEffect(() => {
    if (audioPath === null || playbackRequest === null) {
      playedRequestRef.current = null;
      return;
    }

    if (!enabled) {
      return;
    }

    const requestKey =
      audioPath + "\u0000" + String(playbackRequest);
    if (playedRequestRef.current === requestKey) {
      return;
    }

    playedRequestRef.current = requestKey;
    void playPath(audioPath);
  }, [audioPath, enabled, playbackRequest, playPath]);

  const replay = useCallback(() => {
    if (audioPath !== null) {
      void playPath(audioPath);
    }
  }, [audioPath, playPath]);

  return {
    isPlaying: audioPath !== null && isPlayingPath(audioPath),
    replay
  };
}
